import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/common/Select';
import FormatNumber from '@/components/react-number-format';
import { ChartConfig, ChartContainer, ChartTooltip, ChartTooltipContent } from '@/components/ui/Chart';
import { MONTH_OF_YEAR, SUBMIT_MOMENT_DATE_Y, SUBMIT_MOMENT_DATE_YMD } from '@/configs/constants';
import { useStore } from '@/hooks/useStore';
import { LANGUAGES_SUPPORTED, MONTH_OF_YEAR_TYPE } from '@/types/enums';
import { formatDateTime } from '@/utils/datetime';
import { HouseIcon, HousePlusIcon, LucideIcon, UserPlus, UsersIcon } from 'lucide-react';
import { observer } from 'mobx-react-lite';
import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { CartesianGrid, Line, LineChart, XAxis, YAxis } from 'recharts';
import Tooltip from '@/components/common/Tooltip';
import { convertNumber } from '@/utils/utils';

export default observer(function DashboardPage() {
    // hooks
    const { t, i18n: { language } } = useTranslation();

    // store
    const { dashboardStore: { statisticView, statisticShop, getStatisticViewForDashboard, getStatisticShopForDashboard } } = useStore();

    // state
    const [currentViewYear, setCurrentViewYear] = useState(formatDateTime(new Date(), SUBMIT_MOMENT_DATE_Y) ?? '');
    const [currentShopYear, setCurrentShopYear] = useState(formatDateTime(new Date(), SUBMIT_MOMENT_DATE_Y) ?? '');
    const [yearListViewStatisticForMonthly, setYearListViewStatisticForMonthly] = useState<string[]>([]);
    const [yearListShopStatisticForMonthly, setYearListShopStatisticForMonthly] = useState<string[]>([]);

    // lifecycle
    useEffect(() => {
        getStatisticViewForDashboard(convertPayload('view'));
    }, [currentViewYear]);

    useEffect(() => {
        getStatisticShopForDashboard(convertPayload('shop'));
    }, [currentShopYear]);

    useEffect(() => {
        if (statisticView) {
            setYearListViewStatisticForMonthly(statisticView?.statisticByYear?.years.map(item => item.toString()) ?? []);
        }
    }, [statisticView]);

    useEffect(() => {
        if (statisticShop) {
            setYearListShopStatisticForMonthly(statisticShop?.statisticByYear?.years.map(item => item.toString()) ?? []);
        }
    }, [statisticShop]);

    // function
    const convertPayload = (view: 'view' | 'shop') => {
        return {
            currentDate: formatDateTime(new Date(), SUBMIT_MOMENT_DATE_YMD) ?? '',
            year: (view === 'view' ? currentViewYear : currentShopYear) ?? ''
        };
    };

    const handleChangeSelectViewYear = (value) => {
        setCurrentViewYear(value.toString());
    };

    const handleChangeSelectShopYear = (value) => {
        setCurrentShopYear(value.toString());
    };

    const chartViewConfig = {
        totalViews: {
            label: t('words_title.total_view'),
            color: '#49aef0'
        }
    } satisfies ChartConfig;

    const chartShopConfig = {
        totalShops: {
            label: t('words_title.total_shop'),
            color: '#e27e32'
        }
    } satisfies ChartConfig;

    const chartViewData = useMemo(() => {
        if (statisticView) {
            return Object.values(MONTH_OF_YEAR_TYPE).map((month, index) => {
                return {
                    key: month,
                    month: language === LANGUAGES_SUPPORTED.English ? MONTH_OF_YEAR[month].shortCutEn : MONTH_OF_YEAR[month].shortCutVi,
                    totalViews: statisticView.statisticByYear.data[month - 1]?.totalViews
                };
            });
        }
    }, [statisticView, language]);

    const chartShopData = useMemo(() => {
        if (statisticShop) {
            return Object.values(MONTH_OF_YEAR_TYPE).map((month, index) => {
                return {
                    key: month,
                    month: language === LANGUAGES_SUPPORTED.English ? MONTH_OF_YEAR[month].shortCutEn : MONTH_OF_YEAR[month].shortCutVi,
                    totalShops: statisticShop.statisticByYear.data[month - 1]?.totalShops
                };
            });
        }
    }, [statisticShop, language]);

    return (
        <div className='dashboard-container'>
            <div className='flex items-center gap-2'>
                <DashboardItem label={t('words_title.total_visitor')} value={statisticView?.totalViews ?? 0} icon={UsersIcon} />
                <DashboardItem label={t('words_title.today_visitor')} value={statisticView?.todayViews ?? 0} icon={UserPlus} />
                <DashboardItem label={t('words_title.total_shop')} value={statisticShop?.totalShops ?? 0} icon={HouseIcon} />
                <DashboardItem label={t('words_title.today_shop')} value={statisticShop?.todayNewShops ?? 0} icon={HousePlusIcon} />
            </div>
            <div className='p-4 bg-white rounded mt-4'>
                <div className='flex flex-wrap items-center justify-between gap-4'>
                    <div className='text-lg font-semibold'>{t('words_title.monthly_statistic_by_view')}</div>
                    <Select defaultValue={currentViewYear.toString()} value={currentViewYear} onValueChange={handleChangeSelectViewYear}>
                        <SelectTrigger className='h-10 min-w-32'>
                            <SelectValue>
                            </SelectValue>
                        </SelectTrigger>
                        <SelectContent>
                            {yearListViewStatisticForMonthly?.length > 0 && yearListViewStatisticForMonthly.map((item, index) => (
                                <SelectItem
                                    className='hover:cursor-pointer'
                                    key={index}
                                    value={item.toString()}
                                >
                                    {item}
                                </SelectItem>
                            ))}
                        </SelectContent>
                    </Select>
                </div>
                <ChartContainer config={chartViewConfig} className='mt-8 w-full h-[500px] mx-auto aspect-none'>
                    <LineChart
                        accessibilityLayer
                        data={chartViewData}
                        margin={{
                            left: 4,
                            right: 4
                        }}
                    >
                        <CartesianGrid vertical={false} strokeDasharray='4' />
                        <XAxis
                            dataKey='month'
                            tickLine={false}
                            axisLine={false}
                            tickMargin={8}
                            interval='preserveStartEnd'
                            height={48}
                        />
                        <YAxis
                            domain={([dataMin, dataMax]) => {
                                return [0, dataMax + (dataMax / 10)];
                            }}
                            hide={true}
                        />
                        <ChartTooltip
                            cursor={false}
                            content={(
                                <ChartTooltipContent
                                    className='min-w-40'
                                    labelFormatter={(label, payload) => {
                                        return (
                                            <div>{language === LANGUAGES_SUPPORTED.English ? MONTH_OF_YEAR[payload[0]?.payload?.key].fullNameEn : MONTH_OF_YEAR[payload[0]?.payload?.key].fullNameVi}</div>
                                        );
                                    }}
                                />
                            )}
                        />
                        <Line
                            dataKey='totalViews'
                            type='linear'
                            stroke='#49aef0'
                            strokeWidth={2}
                            dot={true}
                        />
                    </LineChart>
                </ChartContainer>
            </div>
            <div className='p-4 bg-white rounded mt-4'>
                <div className='flex flex-wrap items-center justify-between gap-4'>
                    <div className='text-lg font-semibold'>{t('words_title.monthly_statistic_by_shop')}</div>
                    <Select defaultValue={currentShopYear.toString()} value={currentShopYear} onValueChange={handleChangeSelectShopYear}>
                        <SelectTrigger className='h-10 min-w-32'>
                            <SelectValue>
                            </SelectValue>
                        </SelectTrigger>
                        <SelectContent>
                            {yearListShopStatisticForMonthly?.length > 0 && yearListShopStatisticForMonthly.map((item, index) => (
                                <SelectItem
                                    className='hover:cursor-pointer'
                                    key={index}
                                    value={item.toString()}
                                >
                                    {item}
                                </SelectItem>
                            ))}
                        </SelectContent>
                    </Select>
                </div>
                <ChartContainer config={chartShopConfig} className='mt-8 w-full h-[500px] mx-auto aspect-none'>
                    <LineChart
                        accessibilityLayer
                        data={chartShopData}
                        margin={{
                            left: 4,
                            right: 4
                        }}
                    >
                        <CartesianGrid vertical={false} strokeDasharray='4' />
                        <XAxis
                            dataKey='month'
                            tickLine={false}
                            axisLine={false}
                            tickMargin={8}
                            interval='preserveStartEnd'
                        />
                        <YAxis
                            domain={([dataMin, dataMax]) => {
                                return [0, dataMax + (dataMax / 10)];
                            }}
                            hide={true}
                        />
                        <ChartTooltip
                            cursor={false}
                            content={(
                                <ChartTooltipContent
                                    labelFormatter={(label, payload) => {
                                        return (
                                            <div>{language === LANGUAGES_SUPPORTED.English ? MONTH_OF_YEAR[payload[0]?.payload?.key].fullNameEn : MONTH_OF_YEAR[payload[0]?.payload?.key].fullNameVi}</div>
                                        );
                                    }}
                                    className='min-w-40'
                                />
                            )}
                        />
                        <Line
                            dataKey='totalShops'
                            type='linear'
                            stroke='#e27e32'
                            strokeWidth={2}
                            dot={true}
                        />
                    </LineChart>
                </ChartContainer>
            </div>
        </div>
    );
});

interface DashboardItemProps {
    label: string,
    value: number,
    icon: LucideIcon
}

const DashboardItem = ({ label, value, icon: Icon }: DashboardItemProps) => {
    return (
        <div className='bg-white rounded p-4 w-1/4'>
            <div className='text-text-7 font-medium'>{label}</div>
            <div className='flex items-center justify-between gap-2 mt-2'>
                {
                    value > 999 ?
                        (
                            <Tooltip
                                align='start'
                                content={(
                                    <FormatNumber
                                        type='text'
                                        decimalScale={0}
                                        statusDisplay='text'
                                        thousandSeparator={true}
                                        value={value}
                                        className='border-none bg-transparent items-center m-0 p-0'
                                    />
                                )}
                                triggerClassName='cursor-default'
                            >
                                <div className='h-6.5 font-bold text-[1.5rem] text-active'>
                                    {convertNumber(value)}
                                </div>
                            </Tooltip>
                        ) :
                        (
                            <div className='h-6.5 font-bold text-[1.5rem] text-active'>
                                {convertNumber(value)}
                            </div>
                        )
                }

                <div className='flex items-center justify-center flex-none border border-active rounded p-0.5'>
                    <Icon className='text-active h-5 w-5' />
                </div>
            </div>
        </div>
    );
};
